import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Users
export const usersApi = {
  getAll: () => api.get('/users'),
  getMembers: () => api.get('/users/members'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Goals
export const goalsApi = {
  getAll: () => api.get('/goals'),
  getOne: (id) => api.get(`/goals/${id}`),
  getDetail: (id) => api.get(`/goals/${id}/detail`),
  getDeficit: (id) => api.get(`/goals/${id}/deficit`),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  delete: (id) => api.delete(`/goals/${id}`),
};

// Milestones
export const milestonesApi = {
  getAll: (params) => api.get('/milestones', { params }),
  getOne: (id) => api.get(`/milestones/${id}`),
  create: (data) => api.post('/milestones', data),
  update: (id, data) => api.put(`/milestones/${id}`, data),
  delete: (id) => api.delete(`/milestones/${id}`),
};

// Daily Plans
export const dailyPlansApi = {
  getAll: (params) => api.get('/daily-plans', { params }),
  getOne: (id) => api.get(`/daily-plans/${id}`),
  create: (data) => api.post('/daily-plans', data),
};

// Outcomes
export const outcomesApi = {
  getAll: (params) => api.get('/outcomes', { params }),
  create: (data) => api.post('/outcomes', data),
  update: (id, data) => api.put(`/outcomes/${id}`, data),
  delete: (id) => api.delete(`/outcomes/${id}`),
};

// Tasks
export const tasksApi = {
  getAll: (params) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Meeting Templates
export const meetingTemplatesApi = {
  getAll: () => api.get('/meeting-templates'),
  create: (data) => api.post('/meeting-templates', data),
  update: (id, data) => api.put(`/meeting-templates/${id}`, data),
  delete: (id) => api.delete(`/meeting-templates/${id}`),
};

// Meetings
export const meetingsApi = {
  getAll: () => api.get('/meetings'),
  getOne: (id) => api.get(`/meetings/${id}`),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  delete: (id) => api.delete(`/meetings/${id}`),
};

// Member Meeting Records
export const memberRecordsApi = {
  getAll: (params) => api.get('/member-records', { params }),
  getOne: (id) => api.get(`/member-records/${id}`),
  update: (id, data) => api.put(`/member-records/${id}`, data),
};

// Team Targets
export const teamTargetsApi = {
  getAll: () => api.get('/team-targets'),
  create: (data) => api.post('/team-targets', data),
  update: (id, data) => api.put(`/team-targets/${id}`, data),
  delete: (id) => api.delete(`/team-targets/${id}`),
};

// Dashboard
export const dashboardApi = {
  getMember: () => api.get('/dashboard/member'),
  getAdmin: (horizonFilter) => api.get('/dashboard/admin', { params: { horizon_filter: horizonFilter } }),
};

// Utilities
export const utilsApi = {
  getDepartments: () => api.get('/departments'),
  getHorizons: () => api.get('/horizons'),
  getMilestoneTypes: () => api.get('/milestone-types'),
  getMetricTypes: () => api.get('/metric-types'),
  getMeetingTypes: () => api.get('/meeting-types'),
};

// Platform (Super Admin only)
export const platformApi = {
  getCompanies: () => api.get('/platform/companies'),
  createCompany: (data) => api.post('/platform/companies', data),
  updateCompany: (id, data) => api.patch(`/platform/companies/${id}`, data),
  getCompanyAdmins: (id) => api.get(`/platform/companies/${id}/admins`),
  updateCompanyAdmin: (companyId, data) => api.patch(`/platform/companies/${companyId}/admin`, data),
};

export default api;
